import {
  Add as AddIcon,
  Computer as ComputerIcon,
  ContentCopy as CopyIcon,
  VpnKey as KeyIcon,
  Save as SaveIcon,
} from "@mui/icons-material";
import {
  Alert,
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  List,
  ListItem,
  ListItemText,
  Paper,
  Snackbar,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import React, { useEffect, useState } from "react";
import { useNotification } from "../../contexts/NotificationContext";
import { userProductLicenseService } from "../../services/userProductLicenseService";
import { isLicenseServerAssignment } from "../../utils/licenseServerUtils";

const UserProductHardwareIds = ({
  open,
  onClose,
  user,
  products,
  onGenerateLicense,
}) => {
  const [hardwareIds, setHardwareIds] = useState({});
  const [allHardwareIds, setAllHardwareIds] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [editingProduct, setEditingProduct] = useState(null);
  const [newHardwareId, setNewHardwareId] = useState("");
  const [licenses, setLicenses] = useState({});
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "success",
  });
  const { showNotification } = useNotification();

  useEffect(() => {
    if (open && user) {
      loadUserProductData();
    }
  }, [open, user]);

  const loadUserProductData = async () => {
    if (!user || !user.id) return;

    setLoading(true);
    try {
      // Initialize empty hardware IDs for all assigned products
      const initialHardwareIds = {};
      const initialAllHardwareIds = {};
      const initialLicenses = {};

      // For each product assigned to the user
      for (const product of user.products || []) {
        if (product.requires_license && !isLicenseServerAssignment(product)) {
          try {
            // Try to get current license
            const licenseData =
              await userProductLicenseService.getCurrentLicense(
                user.id,
                product.id,
              );

            if (licenseData && licenseData.license) {
              // Store the primary hardware ID
              initialHardwareIds[product.id] = licenseData.license.hardware_id;

              // Store all hardware IDs
              initialAllHardwareIds[product.id] =
                licenseData.license.hardware_ids ||
                (licenseData.license.hardware_id
                  ? [licenseData.license.hardware_id]
                  : []);

              initialLicenses[product.id] = licenseData.license;
            } else {
              initialHardwareIds[product.id] = "";
              initialAllHardwareIds[product.id] = [];
            }
          } catch (err) {
            console.error(
              `Error fetching license for product ${product.id}:`,
              err,
            );
            initialHardwareIds[product.id] = "";
            initialAllHardwareIds[product.id] = [];
          }
        }
      }

      setHardwareIds(initialHardwareIds);
      setAllHardwareIds(initialAllHardwareIds);
      setLicenses(initialLicenses);
      setError(null);
    } catch (err) {
      console.error("Error loading user product data:", err);
      setError("Failed to load hardware IDs for user products");
    } finally {
      setLoading(false);
    }
  };

  const handleSaveHardwareId = async () => {
    if (!editingProduct || !newHardwareId) return;

    try {
      // Get the current product's licenses
      const response = await userProductLicenseService.getCurrentLicense(
        user.id,
        editingProduct,
      );

      // Add the new hardware ID
      const currentIds = allHardwareIds[editingProduct] || [];
      if (!currentIds.includes(newHardwareId)) {
        // Update the state
        setAllHardwareIds((prev) => ({
          ...prev,
          [editingProduct]: [...currentIds, newHardwareId],
        }));

        // Set as primary hardware ID if none exists
        if (!hardwareIds[editingProduct]) {
          setHardwareIds((prev) => ({
            ...prev,
            [editingProduct]: newHardwareId,
          }));
        }

        // If we have a license, we need to update it with the new hardware ID
        if (response && response.license) {
          // Generate a new license with this hardware ID
          await onGenerateLicense(
            user.id,
            { id: editingProduct, name: "Product" }, // just need id
            newHardwareId,
          );

          setSnackbar({
            open: true,
            message: "Hardware ID added and license updated successfully!",
            severity: "success",
          });
        } else {
          setSnackbar({
            open: true,
            message: "Hardware ID added successfully!",
            severity: "success",
          });
        }
      }
    } catch (err) {
      console.error("Error saving hardware ID:", err);
      setSnackbar({
        open: true,
        message:
          "Error saving hardware ID: " + (err.message || "Unknown error"),
        severity: "error",
      });
    }

    setEditingProduct(null);
    setNewHardwareId("");
  };

  const handleGenerateLicenseClick = (product) => {
    if (!product) return;

    const hardwareId = hardwareIds[product.id] || "";
    if (!hardwareId) {
      alert("Please set a hardware ID first before generating a license.");
      return;
    }

    onGenerateLicense(user.id, product, hardwareId);
  };

  const handleAddHardwareId = (productId) => {
    setEditingProduct(productId);
    setNewHardwareId("");
  };

  const handleCopyLicenseKey = async (license) => {
    if (license && license.license_key) {
      try {
        await navigator.clipboard.writeText(license.license_key);
        showNotification("License key copied successfully!");
      } catch (err) {
        console.error("Failed to copy license key:", err);
        showNotification("Failed to copy license key", "error");
      }
    }
  };

  const handleCloseSnackbar = () => {
    setSnackbar({ ...snackbar, open: false });
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>
        <Box display="flex" alignItems="center" gap={1}>
          <ComputerIcon color="primary" />
          Hardware IDs for {user?.email}
        </Box>
      </DialogTitle>

      <DialogContent>
        {loading ? (
          <Box sx={{ textAlign: "center", py: 3 }}>
            <Typography>Loading hardware IDs...</Typography>
          </Box>
        ) : error ? (
          <Box sx={{ textAlign: "center", py: 3, color: "error.main" }}>
            <Typography>{error}</Typography>
          </Box>
        ) : (
          <Box sx={{ mt: 2 }}>
            <Typography variant="subtitle1" gutterBottom>
              Manage Hardware IDs for Licensed Products
            </Typography>
            <Typography variant="body2" color="text.secondary" paragraph>
              Each licensed product can have multiple hardware IDs. Add or
              update hardware IDs below to generate licenses.
            </Typography>

            <Paper variant="outlined" sx={{ mt: 2 }}>
              <List>
                {user?.products
                  ?.filter((p) => p.requires_license)
                  .map((product, idx) => (
                    <React.Fragment key={product.id}>
                      <ListItem
                        divider={
                          idx <
                          user?.products?.filter((p) => p.requires_license)
                            .length -
                            1
                        }
                        secondaryAction={
                          <Box sx={{ display: "flex", gap: 1 }}>
                            {editingProduct === product.id ? (
                              <IconButton
                                edge="end"
                                color="primary"
                                onClick={handleSaveHardwareId}
                                disabled={!newHardwareId}
                              >
                                <SaveIcon />
                              </IconButton>
                            ) : (
                              <>
                                <IconButton
                                  edge="end"
                                  color="primary"
                                  onClick={() =>
                                    handleAddHardwareId(product.id)
                                  }
                                >
                                  <AddIcon />
                                </IconButton>
                                {!isLicenseServerAssignment(product) && (
                                  <Tooltip title="Generate License">
                                    <IconButton
                                      edge="end"
                                      color="secondary"
                                      onClick={() =>
                                        handleGenerateLicenseClick(product)
                                      }
                                      disabled={!hardwareIds[product.id]}
                                    >
                                      <KeyIcon />
                                    </IconButton>
                                  </Tooltip>
                                )}
                              </>
                            )}
                          </Box>
                        }
                      >
                        <ListItemText
                          primary={
                            <Box
                              sx={{
                                display: "flex",
                                alignItems: "center",
                                gap: 1,
                              }}
                            >
                              <Typography variant="subtitle2">
                                {product.name}
                              </Typography>
                              {isLicenseServerAssignment(product) ? (
                                <Chip
                                  size="small"
                                  color="info"
                                  variant="outlined"
                                  label="License Server"
                                />
                              ) : (
                                licenses[product.id] && (
                                  <Chip
                                    size="small"
                                    color="success"
                                    label="License Active"
                                    icon={<KeyIcon fontSize="small" />}
                                  />
                                )
                              )}
                            </Box>
                          }
                          secondary={
                            <Box sx={{ mt: 1 }}>
                              {editingProduct === product.id ? (
                                <TextField
                                  fullWidth
                                  label="Hardware ID"
                                  value={newHardwareId}
                                  onChange={(e) =>
                                    setNewHardwareId(e.target.value)
                                  }
                                  margin="dense"
                                  placeholder="Enter hardware ID"
                                  autoFocus
                                  required
                                  helperText="Must be a valid base64 string"
                                />
                              ) : (
                                <>
                                  {!isLicenseServerAssignment(product) &&
                                    licenses[product.id] && (
                                      <Box
                                        sx={{
                                          display: "flex",
                                          alignItems: "center",
                                          mb: 1,
                                        }}
                                      >
                                        <Typography
                                          variant="subtitle2"
                                          sx={{ mr: 1 }}
                                        >
                                          License Key:
                                        </Typography>
                                        <Tooltip title="Copy License Key">
                                          <IconButton
                                            size="small"
                                            color="primary"
                                            onClick={() =>
                                              handleCopyLicenseKey(
                                                licenses[product.id],
                                              )
                                            }
                                          >
                                            <CopyIcon fontSize="small" />
                                          </IconButton>
                                        </Tooltip>
                                      </Box>
                                    )}

                                  <Typography variant="subtitle2" gutterBottom>
                                    Hardware IDs:
                                  </Typography>

                                  {allHardwareIds[product.id] &&
                                  allHardwareIds[product.id].length > 0 ? (
                                    allHardwareIds[product.id].map(
                                      (hwId, index) => (
                                        <Box key={index} sx={{ mb: 1 }}>
                                          <Tooltip title={hwId}>
                                            <Typography
                                              sx={{
                                                fontFamily: "monospace",
                                                fontSize: "0.775rem",
                                                whiteSpace: "nowrap",
                                                overflow: "hidden",
                                                textOverflow: "ellipsis",
                                                maxWidth: "400px",
                                                bgcolor: "grey.100",
                                                p: 0.5,
                                                borderRadius: 1,
                                                display: "flex",
                                                alignItems: "center",
                                                justifyContent: "space-between",
                                              }}
                                            >
                                              <span>{hwId}</span>
                                              <IconButton
                                                size="small"
                                                onClick={async () => {
                                                  try {
                                                    await navigator.clipboard.writeText(
                                                      hwId,
                                                    );
                                                    showNotification(
                                                      "Hardware ID copied successfully!",
                                                    );
                                                  } catch (err) {
                                                    console.error(
                                                      "Failed to copy hardware ID:",
                                                      err,
                                                    );
                                                    showNotification(
                                                      "Failed to copy hardware ID",
                                                      "error",
                                                    );
                                                  }
                                                }}
                                                sx={{ ml: 1, p: 0.3 }}
                                              >
                                                <CopyIcon fontSize="small" />
                                              </IconButton>
                                            </Typography>
                                          </Tooltip>
                                        </Box>
                                      ),
                                    )
                                  ) : (
                                    <Typography
                                      color="text.secondary"
                                      variant="body2"
                                    >
                                      No hardware IDs set
                                    </Typography>
                                  )}
                                </>
                              )}
                            </Box>
                          }
                        />
                      </ListItem>
                    </React.Fragment>
                  ))}

                {(!user?.products ||
                  user.products.filter((p) => p.requires_license).length ===
                    0) && (
                  <ListItem>
                    <ListItemText
                      primary="No licensed products assigned"
                      secondary="This user has no products that require a license"
                    />
                  </ListItem>
                )}
              </List>
            </Paper>

            <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
              Note: Hardware IDs are typically base64-encoded strings provided
              by the user's machine. After setting a hardware ID, you can
              generate a license key tied to that specific hardware.
            </Typography>
          </Box>
        )}
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose}>Close</Button>
      </DialogActions>

      {/* Snackbar for notifications */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={3000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert
          onClose={handleCloseSnackbar}
          severity={snackbar.severity}
          sx={{ width: "100%" }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Dialog>
  );
};

export default UserProductHardwareIds;
